// Shown after the user clicks the "reset your password" link in their email
// and lands back in the app. Supabase auto-creates a recovery session; this
// screen captures a new password and calls setPassword.
//
// Renders full-screen (replaces both SignIn and the normal app) until either:
//   - The user successfully sets a new password (then we drop them into the app)
//   - The user signs out (rare — recovery sessions are short-lived)

import { useState, type FormEvent } from "react";
import { useAuth } from "../lib/auth";
import { friendlyError } from "../lib/errors";

export default function PasswordRecovery() {
  const { setPassword, clearPasswordRecovery, signOut } = useAuth();
  const [password, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);

    if (password.length < 10) {
      setError("Password must be at least 10 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    setBusy(true);
    try {
      await setPassword(password);
      // Clear the recovery flag so the AuthGate stops rendering this screen
      // and shows the normal app. The user is already authenticated from the
      // recovery session, so they go straight in.
      clearPasswordRecovery();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full flex flex-col justify-center max-w-md mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-100 tracking-tight">Set a new password</h1>
        <p className="text-slate-400 mt-2 text-sm">
          You're signed in from your recovery link. Pick a new password to finish.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="text-sm text-slate-400">New password</span>
          <input
            type="password"
            required
            minLength={10}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="••••••••••"
            className="mt-1 w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
          />
          <span className="text-xs text-slate-500 mt-1 block">Minimum 10 characters.</span>
        </label>

        <label className="block">
          <span className="text-sm text-slate-400">Confirm new password</span>
          <input
            type="password"
            required
            minLength={10}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••••"
            className="mt-1 w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
          />
        </label>

        {error && (
          <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !password || !confirm}
          className="w-full py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save new password"}
        </button>

        <button
          type="button"
          onClick={async () => {
            await signOut();
            clearPasswordRecovery();
          }}
          className="w-full text-xs text-slate-400 underline"
        >
          Cancel and sign out
        </button>
      </form>
    </div>
  );
}
